import { z } from 'zod';

export const AdPlatformSchema = z.enum(['meta', 'linkedin', 'google']);
export type AdPlatform = z.infer<typeof AdPlatformSchema>;

export const AdRequestStatusSchema = z.enum(['needs_review', 'changes_requested', 'rejected', 'publishing', 'published']);
export type AdRequestStatus = z.infer<typeof AdRequestStatusSchema>;

export const AdRequestSchema = z.object({
  id: z.string(),
  name: z.string(),
  team: z.string(),
  platforms: z.array(AdPlatformSchema),
  requesterEmail: z.string().nullable(),
  audienceName: z.string().nullable(),
  dailyBudgetUsd: z.number(),
  status: AdRequestStatusSchema,
  submittedAt: z.string(),
  reviewFlag: z.string().nullable(),
  creativeCount: z.number(),
});
export type AdRequest = z.infer<typeof AdRequestSchema>;

export const AdRequestListResponseSchema = z.object({ requests: z.array(AdRequestSchema) });

export const PLATFORM_LABELS: Record<AdPlatform, string> = {
  meta: 'Meta',
  linkedin: 'LinkedIn',
  google: 'Google',
};

export const STATUS_LABELS: Record<AdRequestStatus, string> = {
  needs_review: 'Needs review',
  changes_requested: 'Changes requested',
  rejected: 'Rejected',
  publishing: 'Publishing',
  published: 'Created in Meta',
};

export const META_CTA_TYPES = ['SIGN_UP', 'LEARN_MORE', 'APPLY_NOW', 'DOWNLOAD'] as const;
export const MetaCtaTypeSchema = z.enum(META_CTA_TYPES);
export type MetaCtaType = z.infer<typeof MetaCtaTypeSchema>;

export const META_CTA_LABELS: Record<MetaCtaType, string> = {
  SIGN_UP: 'Sign up',
  LEARN_MORE: 'Learn more',
  APPLY_NOW: 'Apply now',
  DOWNLOAD: 'Download',
};

export const MetaStatusResponseSchema = z.object({
  configured: z.boolean(),
  problem: z.string().nullable(),
  account: z.object({ id: z.string(), name: z.string(), currency: z.string(), active: z.boolean() }).nullable(),
  isSandbox: z.boolean(),
  page: z.object({ id: z.string(), name: z.string().nullable(), usable: z.boolean() }).nullable(),
  canPublish: z.boolean(),
});
export type MetaStatus = z.infer<typeof MetaStatusResponseSchema>;

export const MetaAudienceSchema = z.object({
  id: z.string(),
  name: z.string(),
  approximateCount: z.number().nullable(),
  /** Meta's audience subtype, e.g. CUSTOM or LOOKALIKE. */
  subtype: z.string().nullable(),
});
export type MetaAudience = z.infer<typeof MetaAudienceSchema>;

export const PERSONAS = ['Sales', 'RevOps', 'Marketing', 'Founders'] as const;
export const PersonaSchema = z.enum(PERSONAS);
export type Persona = z.infer<typeof PersonaSchema>;

export const MetaAudienceRefSchema = z.object({ id: z.string().regex(/^\d+$/), name: z.string().min(1).max(300) });
export type MetaAudienceRef = z.infer<typeof MetaAudienceRefSchema>;

/** Who an ad set targets and leaves out. A requester who drops a default exclusion says why. */
export const MetaAudienceTargetingSchema = z.object({
  included: z.array(MetaAudienceRefSchema).max(20),
  excluded: z.array(MetaAudienceRefSchema).max(50),
  droppedExclusions: z.array(z.object({ name: z.string().min(1).max(300), reason: z.string().trim().min(3).max(500) })).max(20),
});
export type MetaAudienceTargeting = z.infer<typeof MetaAudienceTargetingSchema>;
export const NO_TARGETING: MetaAudienceTargeting = { included: [], excluded: [], droppedExclusions: [] };
export const MetaAudienceListResponseSchema = z.object({ audiences: z.array(MetaAudienceSchema) });

export const MetaAdObjectKindSchema = z.enum(['campaign', 'adset', 'ad']);
/** A campaign, ad set or ad found by name, to start a request from. */
export const MetaAdSearchResultSchema = z.object({
  kind: MetaAdObjectKindSchema,
  id: z.string(),
  name: z.string(),
  /** The campaign an ad set or ad belongs to. */
  campaignName: z.string().nullable(),
  status: z.string().nullable(),
});
export type MetaAdSearchResult = z.infer<typeof MetaAdSearchResultSchema>;
export const MetaAdSearchResponseSchema = z.object({ results: z.array(MetaAdSearchResultSchema) });

/** An Instant Form on the Page: a lead ad opens it in place of a website. */
export const MetaLeadFormSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdTime: z.string().nullable(),
  /** ACTIVE, ARCHIVED, … */
  status: z.string().nullable(),
});
export type MetaLeadForm = z.infer<typeof MetaLeadFormSchema>;
export const MetaLeadFormListResponseSchema = z.object({ forms: z.array(MetaLeadFormSchema) });

export const MetaMetricsSchema = z.object({
  spendUsd: z.number(),
  impressions: z.number(),
  clicks: z.number(),
  landingPageViews: z.number(),
  leads: z.number(),
});
export type MetaMetrics = z.infer<typeof MetaMetricsSchema>;

export const MetaCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  effectiveStatus: z.string(),
  objective: z.string(),
  dailyBudgetUsd: z.number().nullable(),
  createdTime: z.string(),
  startTime: z.string().nullable(),
  stopTime: z.string().nullable(),
  metrics: MetaMetricsSchema,
});
export type MetaCampaign = z.infer<typeof MetaCampaignSchema>;
export const MetaCampaignListResponseSchema = z.object({ campaigns: z.array(MetaCampaignSchema) });

export const MetaAdSchema = z.object({
  id: z.string(),
  name: z.string(),
  effectiveStatus: z.string(),
  creative: z
    .object({
      title: z.string().nullable(),
      body: z.string().nullable(),
      linkUrl: z.string().nullable(),
      imageUrl: z.string().nullable(),
      callToAction: z.string().nullable(),
    })
    .nullable(),
});
export type MetaAd = z.infer<typeof MetaAdSchema>;

export const MetaDailyMetricsSchema = MetaMetricsSchema.extend({ date: z.string() });
export type MetaDailyMetrics = z.infer<typeof MetaDailyMetricsSchema>;

export const MetaCampaignDetailResponseSchema = z.object({
  campaign: MetaCampaignSchema,
  adSetCount: z.number(),
  ads: z.array(MetaAdSchema),
  daily: z.array(MetaDailyMetricsSchema),
  adsManagerUrl: z.string(),
});
export type MetaCampaignDetail = z.infer<typeof MetaCampaignDetailResponseSchema>;

export const MetaAdPreviewResponseSchema = z.object({ html: z.string().nullable() });

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const endAfterStart = (r: { startDate: string; endDate: string | null }) => r.endDate === null || r.endDate > r.startDate;
const END_AFTER_START = { message: 'End date must be after the start date', path: ['endDate'] };

/** The teams that request ads. */
export const AD_TEAMS = ['Growth', 'Studio', 'Social', 'Events', 'Field marketing'] as const;

export const META_BID_STRATEGIES = ['LOWEST_COST_WITHOUT_CAP', 'COST_CAP'] as const;
export const MetaBidStrategySchema = z.enum(META_BID_STRATEGIES);
export const META_BID_STRATEGY_LABELS: Record<(typeof META_BID_STRATEGIES)[number], string> = {
  LOWEST_COST_WITHOUT_CAP: 'Lowest cost, no cap',
  COST_CAP: 'Cost per result goal',
};

/** Targeting and bidding a reviewer can change per request. */
export const MAX_COUNTRIES = 25;

export const MetaGrowthSettingsSchema = z
  .object({
    countries: z.array(z.string().regex(/^[A-Z]{2}$/)).min(1).max(MAX_COUNTRIES),
    ageMin: z.number().int().min(18).max(65),
    ageMax: z.number().int().min(18).max(65),
    advantageAudience: z.boolean(),
    bidStrategy: MetaBidStrategySchema,
    /** The cost per result goal, only with COST_CAP. */
    bidAmountUsd: z.number().min(0.01).max(10_000).nullable(),
  })
  .refine(s => s.ageMin <= s.ageMax, { message: 'The minimum age must not be above the maximum', path: ['ageMax'] })
  .refine(s => (s.bidStrategy === 'COST_CAP') === (s.bidAmountUsd !== null), {
    message: 'A cost per result goal needs its amount, and lowest cost none',
    path: ['bidAmountUsd'],
  });
export type MetaGrowthSettings = z.infer<typeof MetaGrowthSettingsSchema>;

export const DEFAULT_GROWTH_SETTINGS: MetaGrowthSettings = {
  countries: ['US', 'CA'],
  ageMin: 28,
  ageMax: 60,
  advantageAudience: false,
  bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
  bidAmountUsd: null,
};

const HttpsUrl = z.string().trim().url().startsWith('https://').max(2000);

/** One question of an Instant Form. A duplicated form's other question types pass through as they are. */
export const LeadFormQuestionSchema = z.object({
  type: z.string().regex(/^[A-Z][A-Z0-9_]*$/).max(60),
  key: z.string().max(100).optional(),
  label: z.string().max(200).optional(),
  inlineContext: z.string().max(500).optional(),
  options: z.array(z.object({ key: z.string().max(100), value: z.string().max(300) })).max(50).optional(),
});
export type LeadFormQuestion = z.infer<typeof LeadFormQuestionSchema>;

export const LEAD_FORM_BUTTON_TYPES = ['VIEW_WEBSITE', 'NONE', 'CALL_BUSINESS', 'DOWNLOAD', 'SCHEDULE_APPOINTMENT', 'BOOK_ON_WEBSITE'] as const;

/**
 * An Instant Form as it will be created at publish. Meta's forms can't be changed once created, so
 * the request carries the form's content and publish creates it.
 */
export const MetaLeadFormSpecSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    /** Meta's "Higher intent" form type; false is "More volume". */
    optimizedForQuality: z.boolean(),
    /** The description above the questions ("Contact information"). */
    questionHeadline: z.string().trim().max(300),
    questions: z.array(LeadFormQuestionSchema).min(1).max(20),
    privacyPolicy: z.object({ url: HttpsUrl, linkText: z.string().trim().min(1).max(70) }),
    thankYou: z.object({
      title: z.string().trim().min(1).max(60),
      body: z.string().trim().max(500),
      buttonType: z.enum(LEAD_FORM_BUTTON_TYPES),
      buttonText: z.string().trim().max(60),
      websiteUrl: HttpsUrl.nullable(),
    }),
    /** Meta's Intro greeting; null leaves it off. */
    contextCard: z
      .object({
        title: z.string().trim().max(60),
        style: z.enum(['PARAGRAPH_STYLE', 'LIST_STYLE']),
        content: z.array(z.string().trim().max(500)).max(5),
        buttonText: z.string().trim().max(30),
      })
      .nullable(),
    locale: z.string().regex(/^[A-Z]{2}_[A-Z]{2}$/),
  })
  .refine(s => s.thankYou.buttonType !== 'VIEW_WEBSITE' || s.thankYou.websiteUrl !== null, {
    message: '"Go to website" needs its link',
    path: ['thankYou', 'websiteUrl'],
  });
export type MetaLeadFormSpec = z.infer<typeof MetaLeadFormSpecSchema>;
export const MetaLeadFormSpecResponseSchema = z.object({ spec: MetaLeadFormSpecSchema });

const MetaAdFieldsSchema = z.object({
  name: z.string().trim().min(1).max(200),
  primaryText: z.string().trim().min(1).max(2000),
  headline: z.string().trim().min(1).max(255),
  description: z.string().trim().max(255),
  callToAction: MetaCtaTypeSchema,
  /** '' for a lead ad, which opens its Instant Form instead. */
  destinationUrl: z.union([z.literal(''), z.string().url().startsWith('https://')]),
  /** The Instant Form publish creates; set means the request publishes as a leads campaign. */
  leadFormSpec: MetaLeadFormSpecSchema.nullable(),
  /** The form it was duplicated from, kept for reference. */
  leadFormSourceId: z.string().regex(/^\d+$/).nullable(),
  /** An existing form to use as it is: requests from before forms were built here, and the one publish created. */
  leadFormId: z.string().regex(/^\d+$/).nullable(),
  dailyBudgetUsd: z.number().min(1).max(10_000),
  startDate: IsoDateSchema,
  endDate: IsoDateSchema.nullable(),
  targeting: MetaAudienceTargetingSchema,
  /** Growth's naming: the campaign is "FB Ads - [program] - OBJ:…", the ad set and ads build on it. */
  program: z.string().trim().max(150),
  /** Who it's for; each one's audiences are pre-selected as targets. */
  personas: z.array(PersonaSchema).max(PERSONAS.length),
  /** '' derives it from the program and personas. */
  adSetName: z.string().trim().max(300),
});

const hasDestination = (r: { destinationUrl: string; leadFormId: string | null; leadFormSpec: MetaLeadFormSpec | null }) =>
  (r.leadFormId === null && r.leadFormSpec === null) === (r.destinationUrl !== '');
const DESTINATION = { message: 'Send people to an https website or an Instant Form, not both', path: ['destinationUrl'] };

// For a video ad, imageHash is the uploaded thumbnail and videoId the video in the ad account.
export type MetaPublishRequest = z.infer<typeof MetaAdFieldsSchema> & { growthSettings: MetaGrowthSettings };

export const MetaPublishResponseSchema = z.object({
  campaignId: z.string(),
  adSetId: z.string(),
  /** One per creative, in order; empty when the ads were skipped (sandbox without a Page). */
  ads: z.array(z.object({ creativeName: z.string(), creativeId: z.string(), adId: z.string() })),
  skipped: z.array(z.enum(['creative', 'ad'])),
  /** The Instant Form the ads open, when publish created one. */
  createdLeadFormId: z.string().nullable(),
  adsManagerUrl: z.string(),
});
export type MetaPublishResult = z.infer<typeof MetaPublishResponseSchema>;

export const CreativeTypeSchema = z.enum(['image', 'video']);
export type CreativeType = z.infer<typeof CreativeTypeSchema>;

export const LinkedInRequestFieldsSchema = z.object({
  postUrl: z.string(),
  postUrn: z.string().nullable(),
  campaignGroupId: z.string(),
  campaignGroupName: z.string(),
  sourceCampaignId: z.string(),
  sourceCampaignName: z.string(),
  campaignId: z.string().nullable(),
  creativeId: z.string().nullable(),
});
export type LinkedInRequestFields = z.infer<typeof LinkedInRequestFieldsSchema>;

export const CreativeOverridesSchema = z.object({
  primaryText: z.string().trim().min(1).max(2000).nullable(),
  headline: z.string().trim().min(1).max(255).nullable(),
  description: z.string().trim().max(255).nullable(),
});
export type CreativeOverrides = z.infer<typeof CreativeOverridesSchema>;
export const NO_OVERRIDES: CreativeOverrides = { primaryText: null, headline: null, description: null };

export const RequestCreativeSchema = z.object({
  id: z.string(),
  position: z.number(),
  name: z.string(),
  creativeType: CreativeTypeSchema,
  videoId: z.string().nullable(),
  hasImage: z.boolean(),
  /** The 9:16 version for Stories and Reels: its video (for a video creative) and its image or thumbnail. */
  verticalVideoId: z.string().nullable(),
  hasVerticalImage: z.boolean(),
  overrides: CreativeOverridesSchema,
  metaCreativeId: z.string().nullable(),
  metaAdId: z.string().nullable(),
});
export type RequestCreative = z.infer<typeof RequestCreativeSchema>;

export const AdRequestDetailSchema = AdRequestSchema.extend({
  platform: AdPlatformSchema,
  linkedin: LinkedInRequestFieldsSchema.nullable(),
  creatives: z.array(RequestCreativeSchema),
  primaryText: z.string(),
  headline: z.string(),
  description: z.string(),
  callToAction: MetaCtaTypeSchema,
  destinationUrl: z.string(),
  leadFormId: z.string().nullable(),
  leadFormName: z.string().nullable(),
  leadFormSpec: MetaLeadFormSpecSchema.nullable(),
  leadFormSourceId: z.string().nullable(),
  growthSettings: MetaGrowthSettingsSchema,
  startDate: z.string(),
  endDate: z.string().nullable(),
  targeting: MetaAudienceTargetingSchema,
  program: z.string(),
  personas: z.array(PersonaSchema),
  adSetName: z.string(),
  reviewerEmail: z.string().nullable(),
  reviewNote: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  meta: z
    .object({
      campaignId: z.string(),
      adSetId: z.string().nullable(),
      publishedAt: z.string().nullable(),
    })
    .nullable(),
  publishError: z.string().nullable(),
  canReview: z.boolean(),
  canEdit: z.boolean(),
  canResubmit: z.boolean(),
});
export type AdRequestDetail = z.infer<typeof AdRequestDetailSchema>;
export const AdRequestResponseSchema = z.object({ request: AdRequestDetailSchema });

// Meta allows 50 ads per ad set; a request is a handful of variants (short/long, angle 1/2).
export const MAX_CREATIVES = 10;

export const CreativeSpecSchema = z
  .object({
    /** Set when editing an existing creative; new ones have none. */
    id: z.string().min(1).max(64).optional(),
    name: z.string().trim().min(1).max(100),
    creativeType: CreativeTypeSchema,
    videoId: z.string().regex(/^\d+$/).nullable(),
    /** Whether it has a 9:16 version for Stories and Reels; false clears a saved one. */
    vertical: z.boolean(),
    verticalVideoId: z.string().regex(/^\d+$/).nullable(),
    overrides: CreativeOverridesSchema,
  })
  .refine(c => (c.creativeType === 'video') === (c.videoId !== null), {
    message: 'A video creative needs its uploaded video, and an image creative none',
    path: ['videoId'],
  })
  .refine(c => (c.creativeType === 'video' && c.vertical) === (c.verticalVideoId !== null), {
    message: "A video creative's 9:16 version needs its uploaded video, and an image creative's none",
    path: ['verticalVideoId'],
  });
export type CreativeSpec = z.infer<typeof CreativeSpecSchema>;

export const AdRequestSubmitSchema = MetaAdFieldsSchema.extend({
  team: z.string().trim().max(100),
  leadFormName: z.string().max(300).nullable(),
  /** Only a publisher's are saved; anyone else's are ignored. */
  growthSettings: MetaGrowthSettingsSchema.optional(),
  creatives: z.array(CreativeSpecSchema).min(1).max(MAX_CREATIVES),
})
  .refine(endAfterStart, END_AFTER_START)
  .refine(hasDestination, DESTINATION);
export type AdRequestSubmit = z.infer<typeof AdRequestSubmitSchema>;
/** The creatives' ids, in the order they were sent, so the browser can upload each image. */
export const AdRequestCreatedResponseSchema = z.object({ id: z.string(), creativeIds: z.array(z.string()) });
export const AdRequestUpdatedResponseSchema = z.object({ creativeIds: z.array(z.string()) });

export const AdRequestReviewSchema = z.object({
  decision: z.enum(['changes_requested', 'rejected']),
  note: z.string().trim().min(1).max(2000),
});

const AgentDraftSchema = z.object({
  name: z.string().max(200),
  team: z.string().max(100),
  primaryText: z.string().max(2000),
  headline: z.string().max(255),
  description: z.string().max(255),
  callToAction: MetaCtaTypeSchema,
  destinationUrl: z.string().max(2000),
  /** Set when the ad opens an Instant Form, which then replaces the destination URL. */
  leadFormName: z.string().max(300).nullable(),
  program: z.string().max(150),
  personas: z.array(PersonaSchema),
  /** The target audiences' names. Exclusions are Growth's and not the agent's to change. */
  targetAudienceNames: z.array(z.string().max(300)).max(20),
  /** The Instant Form's description above its questions, when the ad opens a form. */
  formDescription: z.string().max(300).nullable(),
  /** Each creative's name, in order; the ad is named "[program]_[persona]_[creative]". */
  creativeNames: z.array(z.string().max(100)).max(10),
  dailyBudgetUsd: z.string().max(20),
  startDate: z.string().max(10),
  endDate: z.string().max(10),
});

// The draft carries everything decided so far, so the agent only needs the recent turns.
export const AGENT_MAX_MESSAGES = 40;

const ChatMessagesSchema = z
  .array(z.object({ role: z.enum(['user', 'assistant']), text: z.string().trim().min(1).max(4000) }))
  .min(1)
  .max(AGENT_MAX_MESSAGES);
export type ChatMessages = z.infer<typeof ChatMessagesSchema>;

export const AgentTurnRequestSchema = z.object({
  messages: ChatMessagesSchema,
  draft: AgentDraftSchema,
});
export type AgentTurnRequest = z.infer<typeof AgentTurnRequestSchema>;

export const MetaDraftPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    team: z.string().trim().max(100),
    primaryText: z.string().trim().min(1).max(2000),
    headline: z.string().trim().min(1).max(255),
    description: z.string().trim().max(255),
    callToAction: MetaCtaTypeSchema,
    destinationUrl: z.string().trim().max(2000),
    program: z.string().trim().max(150),
    personas: z.array(PersonaSchema).min(1).max(PERSONAS.length),
    audienceIds: z.array(z.string().regex(/^\d+$/)).max(20),
    formDescription: z.string().trim().min(1).max(300),
    creativeNames: z.array(z.string().trim().min(1).max(100)).min(1).max(10),
    dailyBudgetUsd: z.number().min(1).max(10_000),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema.nullable(),
  })
  .partial()
  .strict();
export type MetaDraftPatch = z.infer<typeof MetaDraftPatchSchema>;

export const AgentTurnResponseSchema = z.object({
  reply: z.string(),
  /** `audiences` is `audienceIds` resolved to names on the server. */
  draftPatch: MetaDraftPatchSchema.extend({ audiences: z.array(MetaAudienceRefSchema).optional() }).nullable(),
  choices: z.array(z.string()).nullable(),
});
export type AgentTurnResponse = z.infer<typeof AgentTurnResponseSchema>;

export const AdRequestUpdateSchema = z.object({ request: AdRequestSubmitSchema, resubmit: z.boolean() });
export type AdRequestUpdate = z.infer<typeof AdRequestUpdateSchema>;

export const CampaignAskRequestSchema = z.object({ messages: ChatMessagesSchema });
export const CampaignAskResponseSchema = z.object({ reply: z.string() });

export const CreativeCheckResponseSchema = z.object({
  aspectRatio: z.object({ actual: z.string(), ok: z.boolean(), accepted: z.array(z.string()) }).nullable(),
  summary: z.string(),
  issues: z.array(z.string()),
  suggestion: z
    .object({ primaryText: z.string().optional(), headline: z.string().optional(), description: z.string().optional() })
    .nullable(),
});
export type CreativeCheckResponse = z.infer<typeof CreativeCheckResponseSchema>;

export const CreativeCheckQuerySchema = z.object({
  platform: AdPlatformSchema,
  kind: z.enum(['image', 'video']).default('image'),
  width: z.coerce.number().int().min(1).max(20_000),
  height: z.coerce.number().int().min(1).max(20_000),
});

/** The draft copy travels in a header so the image can be the raw request body. */
export const CREATIVE_DRAFT_HEADER = 'x-adcc-draft';
export const CreativeDraftHeaderSchema = z.object({
  headline: z.string().max(255),
  primaryText: z.string().max(2000),
});

// Meta accepts up to 4 GB; anything near this is far beyond a normal feed ad.
export const MAX_VIDEO_BYTES = 1024 * 1024 * 1024;
export const VIDEO_CHUNK_BYTES = 8 * 1024 * 1024;

export const VideoUploadStartRequestSchema = z.object({ fileSize: z.number().int().min(1).max(MAX_VIDEO_BYTES) });
export const VideoUploadOffsetsSchema = z.object({ startOffset: z.number().int().min(0), endOffset: z.number().int().min(0) });
export const VideoUploadStartResponseSchema = VideoUploadOffsetsSchema.extend({ videoId: z.string(), sessionId: z.string() });
export const VideoChunkQuerySchema = z.object({ startOffset: z.coerce.number().int().min(0) });
export const VideoUploadFinishRequestSchema = z.object({ title: z.string().trim().min(1).max(200) });

export const MetaVideoSchema = z.object({
  id: z.string(),
  state: z.enum(['processing', 'ready', 'error']),
  progress: z.number().nullable(),
  sourceUrl: z.string().nullable(),
  pictureUrl: z.string().nullable(),
  lengthSeconds: z.number().nullable(),
});
export type MetaVideoInfo = z.infer<typeof MetaVideoSchema>;

// A campaign as the Performance table shows it, whatever the platform.
export const PlatformCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  objective: z.string(),
  groupName: z.string().nullable(),
  dailyBudgetUsd: z.number().nullable(),
  startTime: z.string().nullable(),
  stopTime: z.string().nullable(),
  metrics: MetaMetricsSchema,
});
export type PlatformCampaign = z.infer<typeof PlatformCampaignSchema>;

export const LinkedInStatusResponseSchema = z.object({
  configured: z.boolean(),
  problem: z.string().nullable(),
  account: z.object({ id: z.string(), name: z.string(), currency: z.string(), test: z.boolean() }).nullable(),
  canPublish: z.boolean(),
});
export type LinkedInStatus = z.infer<typeof LinkedInStatusResponseSchema>;

export const LinkedInCampaignGroupSchema = z.object({ id: z.string(), name: z.string(), status: z.string() });
export const LinkedInCampaignGroupListResponseSchema = z.object({ groups: z.array(LinkedInCampaignGroupSchema) });
export type LinkedInCampaignGroup = z.infer<typeof LinkedInCampaignGroupSchema>;

// LinkedIn's campaign type; only Sponsored Content (SPONSORED_UPDATES) campaigns can boost a post.
export const BOOSTABLE_CAMPAIGN_TYPE = 'SPONSORED_UPDATES';
export const LinkedInCampaignListResponseSchema = z.object({
  campaigns: z.array(PlatformCampaignSchema.extend({ groupId: z.string(), type: z.string().nullable() })),
});
export type LinkedInCampaign = z.infer<typeof LinkedInCampaignListResponseSchema>['campaigns'][number];

export const LinkedInCampaignDetailResponseSchema = z.object({
  campaign: PlatformCampaignSchema.extend({ groupId: z.string(), type: z.string().nullable() }),
  creatives: z.array(z.object({ id: z.string(), status: z.string(), postUrn: z.string().nullable() })),
  daily: z.array(MetaDailyMetricsSchema),
  campaignManagerUrl: z.string(),
});
export type LinkedInCampaignDetail = z.infer<typeof LinkedInCampaignDetailResponseSchema>;

// A LinkedIn request boosts an existing company post. postUrn is null when this connection couldn't
// read the post at submit (e.g. a test account without page access); publish then re-checks it.
export const LinkedInRequestSubmitSchema = z
  .object({
    platform: z.literal('linkedin'),
    name: z.string().trim().min(1).max(200),
    team: z.string().trim().max(100),
    postUrl: z.string().url().startsWith('https://').max(2000),
    postUrn: z.string().regex(/^urn:li:(share|ugcPost):\d+$/).nullable(),
    campaignGroupId: z.string().regex(/^\d+$/),
    campaignGroupName: z.string().trim().min(1).max(300),
    sourceCampaignId: z.string().regex(/^\d+$/),
    sourceCampaignName: z.string().trim().min(1).max(300),
    dailyBudgetUsd: z.number().min(10).max(10_000),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema.nullable(),
  })
  .refine(endAfterStart, END_AFTER_START);
export type LinkedInRequestSubmit = z.infer<typeof LinkedInRequestSubmitSchema>;

export const LinkedInPostPreviewSchema = z.object({
  urn: z.string(),
  authorUrn: z.string(),
  commentary: z.string(),
  mediaType: z.enum(['image', 'video', 'article', 'none']),
  imageUrl: z.string().nullable(),
});
export const LinkedInPostResolveResponseSchema = z.object({
  post: LinkedInPostPreviewSchema.nullable(),
  /** Why the post couldn't be shown; submission can still go ahead on a test account. */
  problem: z.string().nullable(),
});
export type LinkedInPostResolveResponse = z.infer<typeof LinkedInPostResolveResponseSchema>;

export const LinkedInPublishResponseSchema = z.object({
  campaignId: z.string(),
  creativeId: z.string().nullable(),
  skipped: z.array(z.literal('creative')),
  campaignManagerUrl: z.string(),
});

export const MetaAdImportSchema = z.object({
  /** The ad's name, or the ad set's or campaign's when a whole group was imported. */
  sourceName: z.string(),
  draft: z.object({
    primaryText: z.string(),
    headline: z.string(),
    description: z.string(),
    /** Null when the ad uses a call to action this app doesn't offer. */
    callToAction: MetaCtaTypeSchema.nullable(),
    destinationUrl: z.string(),
    /** The Instant Form a lead ad opens. */
    leadFormId: z.string().nullable(),
    /** The ad set's targeting; dropped exclusions don't apply to an imported ad. */
    targeting: MetaAudienceTargetingSchema,
    dailyBudgetUsd: z.number().nullable(),
  }),
  creatives: z.array(
    z.object({
      name: z.string(),
      /** Every copy Meta offers of the image, or of a video's thumbnail, best first. */
      imageUrls: z.array(z.string()),
      /** A video ad keeps its video, which is already in the ad account's library. */
      video: z.object({ id: z.string(), sourceUrl: z.string().nullable() }).nullable(),
      /** The 9:16 version the ad shows in Stories and Reels, when it customizes media by placement. */
      vertical: z
        .object({ imageUrls: z.array(z.string()), video: z.object({ id: z.string(), sourceUrl: z.string().nullable() }).nullable() })
        .nullable(),
      overrides: z.object({ primaryText: z.string().nullable(), headline: z.string().nullable(), description: z.string().nullable() }),
    }),
  ),
});
export type MetaAdImport = z.infer<typeof MetaAdImportSchema>;
